import { Link } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Briefcase, 
  FolderKanban, 
  MessageSquare, 
  HelpCircle, 
  DollarSign, 
  FileText, 
  Mail,
  LogOut 
} from 'lucide-react';

export const AdminDashboardPage = () => {
  const { logout } = useAdmin();

  const sections = [
    {
      title: 'Services',
      description: 'Manage your service offerings',
      icon: <Briefcase size={32} />,
      link: '/admin/services',
      color: 'bg-blue-500',
    },
    {
      title: 'Projects',
      description: 'Manage case studies and portfolio work',
      icon: <FolderKanban size={32} />,
      link: '/admin/projects',
      color: 'bg-purple-500',
    },
    {
      title: 'Testimonials',
      description: 'Manage client testimonials',
      icon: <MessageSquare size={32} />,
      link: '/admin/testimonials',
      color: 'bg-green-500',
    },
    {
      title: 'FAQs',
      description: 'Manage frequently asked questions',
      icon: <HelpCircle size={32} />,
      link: '/admin/faqs',
      color: 'bg-orange-500',
    },
    {
      title: 'Pricing',
      description: 'Manage pricing packages',
      icon: <DollarSign size={32} />,
      link: '/admin/pricing',
      color: 'bg-emerald-500',
    },
    {
      title: 'About Page',
      description: 'Edit about page content',
      icon: <FileText size={32} />,
      link: '/admin/about',
      color: 'bg-indigo-500',
    },
    {
      title: 'Leads',
      description: 'View submitted leads',
      icon: <Mail size={32} />,
      link: '/admin/leads',
      color: 'bg-pink-500',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1" data-testid="dashboard-heading">
                Admin Dashboard
              </h1>
              <p className="text-slate-600" data-testid="dashboard-description">
                Manage all website content from one place
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" target="_blank">
                <Button variant="outline" className="rounded-full" data-testid="view-site-button">
                  View Site
                </Button>
              </Link>
              <Button 
                onClick={logout} 
                variant="outline" 
                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-2xl p-8 mb-12 text-white">
          <div className="flex items-center gap-4 mb-4">
            <LayoutDashboard size={48} />
            <div>
              <h2 className="font-heading text-2xl font-bold mb-1">Welcome to Content Management</h2>
              <p className="text-green-50">
                Select a section below to start editing your website content
              </p>
            </div>
          </div>
        </div>

        {/* Management Sections Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section, index) => (
            <Link
              key={index}
              to={section.link}
              className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#10B981] hover:shadow-lg transition-all duration-300 group"
              data-testid={`section-card-${index}`}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 ${section.color} rounded-xl text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                {section.icon}
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2" data-testid={`section-title-${index}`}>
                {section.title}
              </h3>
              <p className="text-slate-600" data-testid={`section-description-${index}`}>
                {section.description}
              </p>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 bg-white rounded-2xl p-8 border border-slate-200">
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">Quick Tips</h3>
          <ul className="space-y-3 text-slate-600">
            <li className="flex items-start gap-2">
              <span className="text-[#10B981] font-bold">•</span>
              <span>Click on any section above to manage that content type</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#10B981] font-bold">•</span>
              <span>Changes are saved immediately to the database</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#10B981] font-bold">•</span>
              <span>Use the "View Site" button to see your changes live</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#10B981] font-bold">•</span>
              <span>Remember to logout when you're done</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
